import {
  fetchQuotationLineCatalogueOptions,
  type LineSupplierPrice,
  type QuotationLineCatalogueOptions,
} from '../../lib/quotationApi';
import { getMasterCatalogueItems, type MasterCatalogueItem } from '../../lib/masterCatalogueApi';
import { formatDate } from '../../utils/dateFormat';
import { formatPricingSource } from '../quotations/pricingSourceLabel';

/**
 * One quotation line's catalogue options, as the Master Catalogue screen shows
 * them (BUSINESS_RULES#quotation-line-catalogue-options).
 *
 * The SERVER decides which rows are the options, which of them priced the line,
 * and what pricing recorded about its choice (`quotationLineCatalogueOptions.service`).
 * The rows themselves come through the catalogue's own list endpoint, so they
 * carry exactly the price, pack and group facts every other row on the screen
 * carries — no second decoration path to drift. This file joins the two and
 * words the result.
 */

export async function loadQuotationLineOptions(
  quotationId: string,
  lineNumber: string | number
): Promise<{ options: QuotationLineCatalogueOptions; rows: MasterCatalogueItem[] }> {
  const response = await fetchQuotationLineCatalogueOptions(quotationId, lineNumber);
  const options = response.data;
  if (!options.rowIds.length) return { options, rows: [] };
  const list = await getMasterCatalogueItems({
    ids: options.rowIds,
    page: 1,
    limit: options.rowIds.length,
  });
  return { options, rows: orderRowsByIds(list.data || [], options.rowIds) };
}

/**
 * The rows in the options' order — the priced row first, then the closest names.
 * The list endpoint sorts by its own key, and a row it did not return (deleted
 * between the two requests) is simply absent.
 */
export function orderRowsByIds<T extends { _id: string }>(rows: readonly T[], ids: readonly string[]): T[] {
  const byId = new Map(rows.map((row) => [String(row._id), row]));
  const out: T[] = [];
  for (const id of ids) {
    const row = byId.get(id);
    if (row) out.push(row);
  }
  return out;
}

/**
 * What the card over the grid says, one labelled fact per question.
 *
 * FACTS, NOT SENTENCES. The first version returned a list of sentences, and the
 * owner's review (2026-09-14) was that on some lines they did not make sense: why
 * a row had been chosen was never stated, and "N other rows match this item's
 * name" was printed over rows that plainly were not the item. The server now
 * lists only rows of the same product and sends what pricing recorded about its
 * choice; this says each once, in its own place, and calls out only what a
 * reviewer should check.
 */
export interface LinePricingSummary {
  /** Whether the grid is showing a highlighted row for this line. */
  highlighted: boolean;
  /** What priced the line: a short title and, when there is one, how. */
  pricedFrom: { title: string; detail: string | null };
  /** "Accepted as a substitute" when pricing recorded that; null otherwise. */
  why: string | null;
  /** The reasoning recorded with the choice, verbatim. */
  reasoning: string | null;
  /** The units the pricing step recorded, and the line's unit now — stated, never compared. */
  units: Array<{ label: string; value: string }>;
  /** How many other rows are listed, and what they are. */
  options: { headline: string; detail: string };
  /**
   * The supplier prices pricing weighed (D25): which rule quoted the line, the
   * price it took, and every other supplier's latest price and date. Null when
   * there was nobody else to list.
   */
  supplierPrices: {
    rule: string;
    rows: Array<{ supplier: string; price: string; date: string; mark: string | null }>;
  } | null;
  /** What a reviewer should check. Empty when nothing looks off. */
  cautions: string[];
  /** A standing caveat, for the small print. */
  footnote: string | null;
}

type SupplierChoice = NonNullable<NonNullable<QuotationLineCatalogueOptions['pricedFrom']['choice']>['supplierChoice']>;

/** What a supplier price is dated by (`catalogueVendorChoice.priceDateOf` on the server). */
const PRICE_DATE_WORDS: Record<string, string> = {
  invoice: 'Invoice',
  // "uploaded on", so a produce price reads as the list it came from and the day that
  // list went in (D26, QG-2).
  price_list: 'Price list uploaded on',
  price_edit: 'Price edited',
  added: 'Added',
};

function supplierPriceRow(price: LineSupplierPrice, mark: string | null) {
  const supplier = price.supplier || 'Unnamed supplier';
  return {
    // Across a keyword group the product differs from row to row, so it is named.
    supplier: price.item ? `${price.item} · ${supplier}` : supplier,
    price: price.priceText || 'No price',
    date: price.date
      ? `${PRICE_DATE_WORDS[price.dateBasis ?? ''] ?? 'Dated'} ${formatDate(price.date)}`
      : 'No date on file',
    mark,
  };
}

/**
 * Null when there is nobody to compare with — one supplier quotes without
 * comment, as it does on the line's own remark — unless a client's preferred
 * vendor outranked the price, which is always worth saying.
 */
function supplierPricesFor(choice: SupplierChoice | null): LinePricingSummary['supplierPrices'] {
  const alternatives = choice?.alternatives ?? [];
  const otherProducts = choice?.otherProducts ?? [];
  const group = choice?.group ?? null;
  const pricedByGroup = group?.pricedByGroup === true;
  if (!choice || (alternatives.length === 0 && otherProducts.length === 0 && !pricedByGroup && !choice.preferredDisplaced)) {
    return null;
  }
  const keyword = `"${group?.keyword ?? ''}"`;
  let rule: string;
  if (choice.preferredDisplaced) {
    rule = "The client's preferred vendor quotes this line, over the most recent price.";
  } else if (pricedByGroup) {
    rule = `This line asks for the keyword group ${keyword} and names no brand, so the most recent price in the group quotes it.`;
  } else if (choice.rule === 'most_recent') {
    rule = 'The most recent price quotes this line.';
  } else if (choice.rule === 'no_dated_price') {
    rule = 'No supplier price has a date, so the row matched to this line quotes it.';
  } else {
    rule = 'Chosen by the supplier rule in force when the line was priced.';
  }
  if (!pricedByGroup && otherProducts.length > 0) {
    rule = `${rule} The other products in the keyword group ${keyword} are listed only, because this line names a product.`;
  }
  return {
    rule,
    rows: [
      supplierPriceRow(choice.chosen, choice.preferredDisplaced ? 'Most recent' : 'Quoted'),
      ...alternatives.map((price) => supplierPriceRow(price, null)),
      ...otherProducts.map((price) => supplierPriceRow(price, 'Same group')),
    ],
  };
}

/**
 * `shownRowIds` is what the grid actually holds: a row the server resolved and
 * the list then did not return was deleted in between, and is said to be gone
 * rather than said to be highlighted.
 */
export function describeLinePricing(
  options: QuotationLineCatalogueOptions,
  shownRowIds: readonly string[]
): LinePricingSummary {
  const { pricedFrom, usedRow, line } = options;
  const highlighted = !!usedRow.rowId && shownRowIds.includes(usedRow.rowId);
  const fromCatalogue =
    pricedFrom.how === 'catalogue_row' || pricedFrom.how === 'catalogue_search_pick';
  const sourceLabel = formatPricingSource(pricedFrom.pricingSource);
  // A keyword group recorded beside the line (BUSINESS_RULES#catalogue-keyword-group).
  const recordedGroup =
    pricedFrom.how === 'catalogue_row' ? pricedFrom.choice?.supplierChoice?.group ?? null : null;

  const cautions: string[] = [];
  let title: string;
  let detail: string | null = null;
  let why: string | null = null;
  let reasoning: string | null = null;
  let supplierPrices: LinePricingSummary['supplierPrices'] = null;
  let footnote: string | null = null;

  if (fromCatalogue && highlighted) {
    title = 'The highlighted row';
    if (usedRow.status === 'merged') {
      cautions.push('The row this line was priced from has since been merged into the highlighted row.');
    }
    if (pricedFrom.how === 'catalogue_search_pick') {
      // A person's call. Not second-guessed here, whatever its name.
      detail = 'Picked by hand in the catalogue search';
    } else {
      detail = 'Chosen automatically when the line was priced';
      // An older server sends no `choice`, which reads as nothing recorded.
      const choice = pricedFrom.choice ?? null;
      if (choice?.substitute === true) why = 'Accepted as a substitute';
      reasoning = choice?.reasoning ?? null;
      supplierPrices = supplierPricesFor(choice?.supplierChoice ?? null);
      // Not the same product by today's names: the model took a substitute, or
      // the row or the line was renamed since. Either way, worth a look — unless the
      // line asked for a keyword group and named no brand, where a brand of that
      // group is exactly what was meant, and the rule above says so.
      if (!usedRow.matchesItem && !recordedGroup?.pricedByGroup) {
        cautions.push(
          "The highlighted row doesn't read as the same product as this line. Check it is the right one — it is listed because it priced the line."
        );
      }
    }
    footnote =
      "Rows show the catalogue as it is today. If the row or this line's cost was edited after pricing, the two can differ.";
  } else if (fromCatalogue) {
    title = 'A deleted catalogue row';
    detail = 'The row this line was priced from no longer exists, so nothing is highlighted';
  } else if (pricedFrom.how === 'supplier_pick') {
    title = 'Supplier dropdown';
    detail = [pricedFrom.selectedVendor, sourceLabel].filter(Boolean).join(' · ') || null;
    footnote = "A Supplier dropdown pick doesn't record a catalogue row, so nothing is highlighted.";
  } else if (pricedFrom.how === 'not_priced') {
    title = 'Not priced yet';
    footnote = 'Nothing is highlighted until the line has a price.';
  } else {
    title = sourceLabel || 'Another source';
    detail = 'Not a catalogue row, so nothing is highlighted';
  }

  const others = shownRowIds.filter((id) => id !== usedRow.rowId).length;
  const noun = others === 1 ? 'row' : 'rows';
  const optionsSummary =
    others === 0
      ? {
          headline: highlighted ? 'No other rows' : 'No rows',
          detail: highlighted
            ? 'Nothing else in the catalogue is the same product'
            : 'Nothing in the catalogue is the same product',
        }
      : {
          headline: highlighted ? `${others} other ${noun}` : `${others} ${noun}`,
          detail: recordedGroup
            ? `The same product in any packaging, then every other product in the keyword group "${recordedGroup.keyword ?? ''}"`
            : 'The same product in any packaging, closest name first',
        };

  const units: Array<{ label: string; value: string }> = [];
  if (pricedFrom.units?.referenceUnit) {
    units.push({
      label: pricedFrom.how === 'supplier_pick' ? 'Supplier priced per' : 'Row priced per',
      value: pricedFrom.units.referenceUnit,
    });
  }
  if (pricedFrom.units?.pricedPer) {
    units.push({ label: 'Line priced per', value: pricedFrom.units.pricedPer });
  }
  if (line.uom && (units.length > 0 || fromCatalogue)) {
    units.push({ label: 'Line unit now', value: line.uom });
  }

  return {
    highlighted,
    pricedFrom: { title, detail },
    why,
    reasoning,
    units,
    options: optionsSummary,
    supplierPrices,
    cautions,
    footnote,
  };
}

/** The card's words for a refusal — chosen by the envelope's code, never its prose (invariant 6). */
export function lineOptionsErrorMessage(error: unknown, lineNumber: string | number): string {
  const code = error && typeof error === 'object' ? (error as { code?: unknown }).code : undefined;
  switch (code) {
    case 'QUOTATION_NOT_FOUND':
      return 'That quotation no longer exists.';
    case 'QUOTATION_LINE_INVALID':
      return 'This link does not name a quotation line.';
    case 'QUOTATION_LINE_NOT_FOUND':
      return `The quotation has no line ${lineNumber} any more — removing a line renumbers the ones after it. Open the link again from the quotation.`;
    case 'QUOTATION_LINE_AMBIGUOUS':
      return `More than one line on this quotation is numbered ${lineNumber}, so this link cannot tell which one you meant.`;
    case 'PERMISSION_DENIED':
      return 'You need access to Quotations to see which catalogue row priced this line.';
    default:
      return "This line's catalogue options could not be loaded. Reload the page to try again.";
  }
}
